import { toJS, when } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { toast } from '@feathr/components';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';

import { useIframeMessaging } from '../../../hooks';
import { useEmailConfigurator } from '../useEmailConfigurator';
import type { IGoldenGooseConfig } from './EmailConfiguratorIframe.utils';
import {
  fromEditorConfig,
  getEmailConfiguratorUrl,
  toEditorConfig,
} from './EmailConfiguratorIframe.utils';

import * as styles from './EmailConfiguratorIframe.css';

interface IEmailConfigReadyMessage {
  type: 'feathr:email-config:ready';
}

interface IEmailConfigMessage {
  type: 'feathr:email-config:config';
  config: IGoldenGooseConfig;
  isDirty?: boolean;
}

interface ISendTestEmailMessage {
  type: 'feathr:email-config:send-test';
  email: string;
}

interface IRequestNavigationMessage {
  type: 'feathr:email-config:request-navigation';
  path: string;
}

function isEmailConfigReadyMessage(data: unknown): data is IEmailConfigReadyMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:email-config:ready'
  );
}

function isEmailConfigMessage(data: unknown): data is IEmailConfigMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:email-config:config'
  );
}

function isSendTestEmailMessage(data: unknown): data is ISendTestEmailMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:email-config:send-test'
  );
}

function isRequestNavigationMessage(data: unknown): data is IRequestNavigationMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:email-config:request-navigation'
  );
}

function EmailConfiguratorIframe(): JSX.Element {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { initialConfig, missingSetupFields, setEditorConfig, setEditorOrganization } =
    useEmailConfigurator();
  const account = useAccount();
  const { EmailVerifications } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null);
  const [approvedEmails, setApprovedEmails] = useState<string[]>([]);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const { url: editorUrl, origin: editorOrigin } = getEmailConfiguratorUrl();

  useEffect(() => {
    async function fetchTemplate(): Promise<void> {
      try {
        const html = await account.fetchDonationReceiptPreview();
        if (html) {
          setPreviewTemplate(html);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[EmailConfigurator] Failed to load preview template:', error);
      } finally {
        setTemplateLoaded(true);
      }
    }

    fetchTemplate();
  }, [account]);

  // Fetch verified email addresses for the sender select dropdown
  useEffect(() => {
    async function fetchApprovedEmails(): Promise<void> {
      try {
        const results = EmailVerifications.list({
          filters: { status: 'Success' },
        });
        await when(() => !results.isPending);
        const emails = results.models
          .map((model) => model.get('email'))
          .filter((email): email is string => !!email);
        setApprovedEmails(emails);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[EmailConfigurator] Failed to load approved emails:', error);
      }
    }

    fetchApprovedEmails();
  }, [EmailVerifications]);

  const sendConfigToEditor = useCallback((): void => {
    const config = toEditorConfig(toJS(initialConfig), account);

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:email-config:init',
        config,
        accountId: account.id,
        previewTemplate,
        approvedEmails,
        setupCompleted: missingSetupFields.length === 0,
        missingSetupFields,
      },
      editorOrigin,
    );
  }, [initialConfig, editorOrigin, account, previewTemplate, approvedEmails, missingSetupFields]);

  const sendTestEmail = useCallback(
    async (email: string): Promise<void> => {
      const result = await account.sendTestDonationReceipt(email);

      if (result.success) {
        toast(t('Test receipt sent!\nSent to 1 email: {{email}}', { email }), { type: 'success' });
      }

      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'feathr:email-config:send-test-result',
          success: result.success,
          message: result.message,
          error: result.error,
        },
        editorOrigin,
      );
    },
    [account, editorOrigin],
  );

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      if (isEmailConfigReadyMessage(event.data)) {
        setEditorReady(true);
        return;
      }

      if (isEmailConfigMessage(event.data)) {
        const isDirty = event.data.isDirty ?? false;
        const { receiptSettings, organization } = fromEditorConfig(event.data.config);
        setEditorConfig(receiptSettings, isDirty);
        if (organization) {
          setEditorOrganization(organization, isDirty);
        }
        return;
      }

      if (isSendTestEmailMessage(event.data)) {
        sendTestEmail(event.data.email);
        return;
      }

      if (isRequestNavigationMessage(event.data)) {
        history.push(localUrl(event.data.path));
      }
    },
    [setEditorConfig, setEditorOrganization, sendTestEmail, history, localUrl],
  );

  useIframeMessaging({
    origin: editorOrigin,
    onMessage: handleMessage,
    sendConfig: sendConfigToEditor,
    iframeRef,
  });

  // Send config to editor when editor is ready AND template fetch has completed (success or fail)
  useEffect(() => {
    if (editorReady && templateLoaded) {
      sendConfigToEditor();
    }
  }, [editorReady, templateLoaded, sendConfigToEditor]);

  // Notify iframe of setup status after save (updates tooltip and enables/disables button).
  // Skip the initial render since the init message already carries this data.
  const setupStatusInitRef = useRef(false);
  useEffect(() => {
    if (!setupStatusInitRef.current) {
      setupStatusInitRef.current = true;
      return;
    }
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:email-config:setup-status',
        setupCompleted: missingSetupFields.length === 0,
        missingSetupFields,
      },
      editorOrigin,
    );
  }, [missingSetupFields, editorOrigin]);

  return (
    <div className={styles.root}>
      <iframe
        className={styles.iframe}
        ref={iframeRef}
        src={editorUrl}
        title={'Email Configurator'}
      />
    </div>
  );
}

export default EmailConfiguratorIframe;
